import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// Default role permissions (fallback if API fails)
const DEFAULT_ROLE_PERMISSIONS = {
  admin: ['*'],
  seller: ['dashboard.view', 'orders.view', 'orders.manage', 'returns.view', 'returns.manage', 'products.view', 'products.create', 'products.edit', 'products.delete', 'inventory.view', 'inventory.manage', 'warehouses.view', 'warehouses.manage', 'reconciliation.view', 'reconciliation.claims', 'reports.view', 'users.view', 'users.manage', 'settings.view', 'settings.manage', 'kyc.view', 'kyc.submit', 'roles.view', 'roles.manage'],
  manager: ['dashboard.view', 'orders.view', 'orders.manage', 'returns.view', 'returns.manage', 'products.view', 'products.create', 'products.edit', 'inventory.view', 'inventory.manage', 'warehouses.view', 'reconciliation.view', 'reports.view', 'kyc.view'],
  staff: ['dashboard.view', 'orders.view', 'products.view', 'inventory.view', 'warehouses.view'],
  viewer: ['dashboard.view', 'orders.view', 'returns.view', 'products.view', 'inventory.view', 'warehouses.view', 'reconciliation.view', 'reports.view']
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      
      // Fetch user permissions
      try {
        const permsRes = await axios.get(`${API_URL}/api/rbac/user-permissions`);
        setPermissions(permsRes.data.permissions || []);
      } catch {
        // Fallback to default permissions based on role
        const role = response.data.role || 'staff';
        setPermissions(DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.staff);
      }
    } catch (error) {
      setUser(false);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    setUser(response.data);
    
    // Fetch permissions after login
    try {
      const permsRes = await axios.get(`${API_URL}/api/rbac/user-permissions`);
      setPermissions(permsRes.data.permissions || []);
    } catch {
      const role = response.data.role || 'staff';
      setPermissions(DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.staff);
    }
    
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    setUser(response.data);
    setPermissions(DEFAULT_ROLE_PERMISSIONS.seller);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
    setPermissions([]);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeGoogleSession = async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/google/session`, { session_id: sessionId });
    setUser(response.data);
    setPermissions(DEFAULT_ROLE_PERMISSIONS.seller);
    return response.data;
  };

  const refreshAuth = () => {
    checkAuth();
  };

  // Permission check helper
  const hasPermission = (permission) => {
    if (user?.role === 'admin') return true;
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  };

  // Check multiple permissions (any)
  const hasAnyPermission = (perms) => {
    return perms.some(p => hasPermission(p));
  };

  // Check multiple permissions (all)
  const hasAllPermissions = (perms) => {
    return perms.every(p => hasPermission(p));
  };

  const value = {
    user,
    loading,
    permissions,
    login,
    register,
    logout,
    googleLogin,
    exchangeGoogleSession,
    refreshAuth,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAuthenticated: !!user && user !== false,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Format API error detail for display
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
